import { createSelector } from '@reduxjs/toolkit'
import { shallowEqual } from 'react-redux'

import { type CardIdType, type CardRefType } from '@/defs/card'
import { type NonEmptyArray } from '@/defs/misc'
import {
  DEFAULT_PRICE_SOURCES,
  DEFAULT_PRICE_SOURCES_CA,
  DEFAULT_PRICE_SOURCES_EU,
  EU_COUNTRIES,
  isPriceSourceType,
  type PriceSourceType,
} from '@/defs/price'
import { parseCardId } from '@/utils/cardUtil'

import { type ReduxRootState } from './store'

const isRehydrated = (s: ReduxRootState) => s.view.isRehydrated

const priceSources = createSelector(
  [(s: ReduxRootState) => s.user, (_, country?: string | null) => country],
  (user, country) => {
    const userSources = user.status === 'signedIn' ? user.profile.priceSources.filter(isPriceSourceType) : []

    const defaults =
      country === 'CA'
        ? DEFAULT_PRICE_SOURCES_CA
        : country && EU_COUNTRIES.includes(country)
          ? DEFAULT_PRICE_SOURCES_EU
          : DEFAULT_PRICE_SOURCES

    return [...new Set<PriceSourceType>([...userSources, ...defaults])] as NonEmptyArray<PriceSourceType>
  },
  { memoizeOptions: { resultEqualityCheck: shallowEqual } },
)

const patreonTier = (s: ReduxRootState) => {
  if (s.user.status !== 'signedIn') return false
  if (s.user.profile.isAdmin) return 'rare'
  const patreon = s.user.profile.patreon
  if (patreon?.patron_status === 'active_patron') {
    if (patreon.currently_entitled_amount_cents >= 500) return 'rare'
    else if (patreon.currently_entitled_amount_cents >= 200) return 'uncommon'
  }
  return false
}

const alternateArt = createSelector(
  [(s: ReduxRootState) => s, (_, cardId?: CardIdType | null) => cardId],
  (s, cardId) => (patreonTier(s) && cardId && s.user.profile?.alternateArt?.[parseCardId(cardId).slug]) || '',
)

const clipboardCardIdsEncoded = createSelector(
  [(s: ReduxRootState) => s.clipboard.cards, (_, cardRefs: CardRefType[]) => cardRefs],
  (cards, cardRefs) => cardRefs.map(({ cardId }) => (cards.some((c) => c.cardId === cardId) ? '1' : '0')).join(''),
)

export const selectors = {
  alternateArt,
  clipboardCardIdsEncoded,
  isRehydrated,
  patreonTier,
  priceSources,
}
